import React, { useState } from "react";
import axios from "axios";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  Group,
  Flex,
  Title,
  Text,
  useMantineTheme,
  Divider,
} from "@mantine/core";
import { IconBug, IconCircleCheck } from "@tabler/icons-react";
import DeviceDetector from "device-detector-js";
import { useTranslation } from "react-i18next";
import { BasicForm } from "@components/shared";
import { phoneValidation } from "@util/validation";

export default function TicketEntryForm({
  firstName = "",
  lastName = "",
  email = "",
  phone = "",
  description = "",
  reqData = null,
  isManager = false,
  showTitle = true,
  issueLabel = "",
}) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const deviceDetector = new DeviceDetector();
  const userAgent =
    window && window.navigator ? window.navigator.userAgent : null;
  const device = userAgent ? deviceDetector.parse(userAgent) : null;

  const formattedIssueLabel =
    issueLabel === "" ? t("mixer:trouble_ticket:issue_label") : issueLabel;

  function onSubmit(formData) {
    setLoading(true);

    let req = {
      ...formData,
    };

    if (reqData) {
      req = {
        ...req,
        ...reqData,
      };
    }

    if (device) req.device = device;

    axios
      .post(`/trouble-tickets/`, req)
      .then(() => {
        setLoading(false);
        setSuccess(true);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "first_name",
      label: t("common:first_name"),
      initialValue: firstName,
      required: true,
      schema: () =>
        Yup.string()
          .max(50, t("validation:too_long"))
          .required(t("validation:required")),
    },
    {
      name: "last_name",
      label: t("common:last_name"),
      initialValue: lastName,
      required: true,
      schema: () =>
        Yup.string()
          .max(50, t("validation:too_long"))
          .required(t("validation:required")),
    },
    {
      name: "email",
      label: t("mixer:trouble_ticket:email_label"),
      initialValue: email,
      required: true,
      schema: () =>
        Yup.string()
          .max(50, t("validation:too_long"))
          .email(t("validation:email_invalid"))
          .required(t("validation:required")),
    },
    {
      name: "phone",
      label: t("mixer:trouble_ticket:phone_label"),
      initialValue: phone,
      mask: {
        pattern: "(999) 999-9999",
        placeholder: "(XXX) XXX-XXXX",
      },
      required: true,
      schema: () =>
        Yup.string()
          .matches(phoneValidation, t("validation:phone_invalid"))
          .required(t("validation:required")),
    },
    {
      name: "issue_description",
      label: formattedIssueLabel,
      initialValue: description,
      textarea: true,
      required: true,
      schema: () =>
        Yup.string()
          .max(255, t("validation:maximum_characters", { count: 255 }))
          .required(t("validation:required")),
    },
  ].filter((f) =>
    isManager
      ? !["first_name", "last_name", "email", "phone"].includes(f.name)
      : true
  );

  return (
    <React.Fragment>
      {success ? (
        <React.Fragment>
          <TitleSection
            title={t("common:thank_you")}
            subtitle={t("mixer:trouble_ticket:success_message")}
            icon={<IconCircleCheck size={60} color={theme.colors.green[6]} />}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          {showTitle && (
            <>
              <TitleSection
                title={t("mixer:trouble_ticket:form_title")}
                subtitle={t("mixer:trouble_ticket:form_subtitle")}
                icon={<IconBug size={60} color={theme.colors.red[6]} />}
              />
              <Divider mt="sm" mb="xs" variant="dashed" />
            </>
          )}
          <BasicForm
            loading={loading}
            onSubmit={onSubmit}
            fields={fields}
            buttonText={t("common:submit")}
            buttonProps={{
              fullWidth: true,
              color: "green",
            }}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

const TitleSection = ({ title, subtitle, icon }) => {
  return (
    <Flex>
      <div style={{ flexGrow: 1 }}>
        <Title>{title}</Title>
        <Text>{subtitle}</Text>
      </div>
      <Group>{icon}</Group>
    </Flex>
  );
};
